<template>
  <b-modal
    id="organizerMessageModal"
    v-model="form.visible"
    hide-backdrop
    :content-class="`shadow-lg organizer-message-content${isHebrew ? ' organizer-message-rtl' : ''}`"
    :title="form.mode === 'add' ? t('organizerMessageAddTitle') : t('organizerMessageEditTitle')"
    :ok-title="t('organizerMessageSaveButton')"
    :cancel-title="t('organizerMessageCancelButton')"
    :ok-disabled="form.saving"
    @ok.prevent="saveOrganizerMessage"
  >
    <div class="organizer-message-form" :dir="isHebrew ? 'rtl' : 'ltr'">
      <b-form-group :label="t('organizerRecipientLabel')">
        <b-form-select
          v-model="form.recipientType"
          :options="organizerRecipientOptions"
          :disabled="form.mode !== 'add'"
        />
        <!-- Fixed after creation: travellers may already hold a button for
             this message, or the manager may already have been notified. The
             server refuses a change with 400 recipient_type_immutable; this
             is the courtesy. -->
        <small class="organizer-message-time-help">
          {{ form.mode === 'add' ? t('organizerRecipientHelp')
                                 : t('organizerRecipientFixedHelp') }}
        </small>
        <!-- Only when we KNOW he is unlinked (managerLinked === false, never
             null): the server refuses the save with 409
             manager_whatsapp_not_linked, and without this he would meet that
             refusal only after writing the whole message, with no idea where
             to fix it. Opens the account-level popup ON TOP of this one and
             navigates nowhere: linking is an account setting, and routing
             away would throw out the half-typed message, which is the whole
             reason the hint exists. -->
        <small v-if="form.recipientType === 'ride_manager'
                 && managerLinked === false"
               class="organizer-message-not-linked">
          {{ t('organizerRecipientNotLinkedHint') }}
          <a href="#" @click.prevent="openMyWhatsappNumber">
            {{ t('organizerRecipientLinkNowLink') }}
          </a>
        </small>
      </b-form-group>

      <b-form-group :label="t('organizerMessageDateLabel')">
        <input
          type="date"
          class="form-control"
          v-model="form.dayDate"
          :min="organizerMessageDateBounds.min"
          :max="organizerMessageDateBounds.max"
          @click="openNativePicker"
        >
        <small class="organizer-message-time-help">
          {{ t('organizerMessageDateHelp') }}
        </small>
      </b-form-group>

      <b-form-group :label="t('organizerMessageBodyLabel')">
        <b-form-textarea
          v-model="form.body"
          :placeholder="t('organizerMessageBodyPlaceholder')"
          rows="4"
          maxlength="4096"
        />
      </b-form-group>

      <b-form-group :label="t('organizerMessageTimeLabel')">
        <input
          type="time"
          step="600"
          class="form-control organizer-message-time-input"
          v-model="form.sendTime"
          @click="openNativePicker"
        >
        <small class="organizer-message-time-help">{{ t('organizerMessageTimeHelp') }}</small>
      </b-form-group>

      <!-- The default option reads "this day's local time", which is true for
           a message to TRAVELLERS and not true for one addressed to the
           manager: the backend keys this on the RECIPIENT and resolves his
           own note in DEFAULT_TIMEZONE, because a manager reading his own
           reminder is at home, not wherever that trip day happens to be.
           Saying so here is the difference between "08:00" meaning 08:00 and
           meaning 01:00. -->
      <b-form-group :label="t('organizerMessageTimezoneLabel')">
        <b-form-select
          v-model="form.timezone"
          :options="organizerMessageTimezoneOptions"
        />
        <small v-if="form.recipientType === 'ride_manager'"
               class="organizer-message-time-help">
          {{ t('organizerMessageTimezoneManagerHelp') }}
        </small>
      </b-form-group>

      <p v-if="form.error" class="organizer-message-form-error">
        {{ form.error }}
      </p>
    </div>
  </b-modal>
</template>

<script>
// Shared organizer-message modal: extracted out of TripCalendarView.vue so
// the calendar tab and the list view (SingleRide.vue) can both open the
// same form instead of maintaining two copies of its subtle rules (see
// buildOrganizerMessagePayload, originalDayDate, the always-sent edit
// timezone, and the double-submit guard below -- each one is a bug that
// was already found and fixed once).
//
// Callers reach this component through $refs:
//   this.$refs.organizerMessageModal.openForDate(dateStr)   -- add, day preselected
//   this.$refs.organizerMessageModal.openForMessage(message) -- edit
// and listen for:
//   @saved="(message) => ..."   -- the server's row, for add AND edit
// so each parent can upsert its own local copy (see upsertOrganizerMessage
// in organizerMessagesMixin.js, which both TripCalendarView.vue and
// SingleRide.vue also mix in for the fetch/delete/gate/date-derivation side
// of this feature -- this component owns only the form and the modal).
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import { translationMixin } from '@/mixins/translationMixin';
import { buildOrganizerMessagePayload } from '@/utils/organizerMessagePayload';
import whatsappAccountLinkApi from '@/api/whatsappAccountLink';
import { mapOrganizerMessageError } from '@/utils/organizerMessageErrors';
import { TIMEZONE_GROUPS, offsetLabel } from '@/common/timezones';
import organizerMessagesApi from '@/api/organizerMessages';

// Mirrors ORGANIZER_DAYS_AFTER_RIDE in travelerblog/whatsapp/organizer.py,
// which is the authority: the API refuses anything after that window and
// the due scan re-checks it even then, because a manager can move the
// trip's dates after writing a message. It bounds the modal's DATE FIELD,
// so a manager can reach the days after the trip -- the "thank you for
// coming" case -- without the calendar having to render days it does not
// own. If the backend's number changes, change this. It MUST stay equal to
// the Python constant -- a picker that offers a date the API refuses hands
// the manager a 400 with nothing to do about it.
//
// There is no bound BEFORE the trip since 2026-11-02 (owner's decision):
// the field starts at today, so any day from now until the trip is open.
const ORGANIZER_DAYS_AFTER_RIDE = 7;

export default {
  name: 'OrganizerMessageModal',
  mixins: [translationMixin],
  data() {
    return {
      // null = not asked yet, true/false = the answer. Deliberately
      // three-valued: `false` renders a "you are not linked" warning, and
      // showing that before we have asked would be a claim we cannot back.
      managerLinked: null,
      form: {
        visible: false,
        mode: 'add', // 'add' | 'edit'
        originalDayDate: '',
        id: null,
        // YYYY-MM-DD, and EDITABLE -- the date field in the modal writes
        // here, whichever entry point opened it. originalDayDate above keeps
        // what it opened WITH, and buildOrganizerMessagePayload sends
        // target_date on edit only when the two differ. Do not "simplify"
        // originalDayDate away on the assumption this is fixed: that is how
        // the 400 date_outside_window on a typo fix comes back, whose only
        // escape was delete-and-recreate -- a second billed send to every
        // traveller who already got the first.
        dayDate: '',
        body: '',
        sendTime: '08:00',
        // '' = that day's local time. How it reaches the server differs by
        // endpoint, which is the whole point of organizerMessagePayload.js:
        // CREATE omits the key (absent == NULL), EDIT sends '' explicitly
        // (the PUT handler is PATCH-shaped, so an absent key would keep the
        // old zone and the message would fire at the wrong hour).
        timezone: '',
        // 'registered_users' | 'ride_manager'. Chosen on create only; the
        // payload builder never sends it on edit.
        recipientType: 'registered_users',
        saving: false,
        error: ''
      }
    };
  },
  computed: {

    ...mapGetters([
      'allRide',
      'apptoken',
      'isRideLocked',
      // Watched below: the linking popup is where the answer to "is he
      // linked?" actually changes, so its closing is our cue to re-ask.
      'modals'
    ]),

    organizerRecipientOptions() {
      return [
        { value: 'registered_users',
          text: this.t( 'organizerRecipientTravellers' ) },
        { value: 'ride_manager',
          text: this.t( 'organizerRecipientManager' ) },
      ];
    },

    organizerMessageTimezoneOptions() {
      const options = [
        { value: '', text: this.t('organizerMessageTimezoneDefault') }
      ];
      TIMEZONE_GROUPS.forEach((group) => {
        options.push({
          label: this.t(`whatsappReminderTimezoneGroup_${group.key}`),
          options: group.zones.map((zone) => ({
            value: zone.value,
            text: this.zoneLabel(zone.value)
          }))
        });
      });
      return options;
    },

    organizerMessageDateBounds() {
      // From today up to ORGANIZER_DAYS_AFTER_RIDE after the trip. The max
      // is enforced here only as a courtesy -- organizer.date_in_window is
      // the authority and refuses anything later with date_outside_window,
      // and the due scan re-checks even that, because a manager can move
      // the trip's dates after writing a message. The min is this field's
      // alone: the API has no lower bound, and a date already past would
      // simply never fire.
      const start = this.allRide?.ride?.start_date;
      if (!start) return { min: null, max: null };
      const startDate = moment(start, 'DD-MM-YYYY');
      const endDate = this.allRide?.ride?.end_date
        ? moment(this.allRide.ride.end_date, 'DD-MM-YYYY')
        : startDate.clone();
      return {
        min: moment().format('YYYY-MM-DD'),
        max: endDate.clone().add(ORGANIZER_DAYS_AFTER_RIDE, 'days')
          .format('YYYY-MM-DD')
      };
    }
  },
  watch: {
    'form.recipientType'( value ) {
      if ( value === 'ride_manager' ) this.refreshManagerLinked();
    },

    // He opened the linking popup FROM this warning, linked, and closed it --
    // the warning must not still be accusing him. Watching the popup's own
    // flag rather than adding a cross-component signal: the store already
    // knows when it closes, and this composer stays open underneath the
    // whole time, so there is no other moment to re-ask. Forced through null
    // because refreshManagerLinked is a no-op on a known answer.
    'modals.myWhatsappNumber'( isOpen, wasOpen ) {
      if ( wasOpen && !isOpen && this.form.recipientType === 'ride_manager' ) {
        this.managerLinked = null;
        this.refreshManagerLinked();
      }
    }
  },
  methods: {
    openMyWhatsappNumber() {
      // No navigation at all: the linking UI is an account-level popup, so
      // it opens over this modal and the half-typed message is still here
      // when it closes.
      this.showModal( 'myWhatsappNumber' );
    },

    async refreshManagerLinked() {
      // Self-scoped: it asks about the CALLER's own WhatsApp, so it needs no
      // ride and tells him nothing about anyone else. Asked once per modal
      // opening rather than on every keystroke.
      if ( this.managerLinked !== null ) return;
      try {
        const response = await whatsappAccountLinkApi.getLink( this.apptoken );
        this.managerLinked = !!( response.data && response.data.linked );
      } catch ( error ) {
        // UNKNOWN, not "unlinked": a failed lookup must not accuse him of
        // not having linked. The warning stays hidden and the server's 409
        // remains the backstop -- which also sets this to false, below.
        this.managerLinked = null;
      }
    },

    ...mapActions([
      'showModalWithParameters',
      'showModal'
    ]),

    // Open the browser's own date/time picker when the manager clicks
    // ANYWHERE in the field, not only the small icon at its edge.
    //
    // By default a native <input type="date"|"time"> only opens its picker
    // from that icon; clicking the rest of the box just puts a caret in a
    // segment and waits for typing. On a time field that is a particularly
    // bad trade -- the icon is a ~20px target next to a 180px box, and
    // typing a time means getting the segment and the AM/PM right.
    //
    // showPicker() needs a user gesture, which a click is. It throws rather
    // than returns a flag when the browser refuses (no gesture, or a
    // cross-origin frame), and it does not exist at all before Chrome 99 /
    // Firefox 101 / Safari 16 -- so it is feature-detected AND wrapped. In
    // every failing case the field is still a working input that can be
    // typed into or opened from the icon, so there is nothing to fall back
    // to and nothing to report.
    openNativePicker( event ) {
      const input = event.target;
      if ( !input || typeof input.showPicker !== 'function' ) return;
      try {
        input.showPicker();
      } catch ( error ) {
        // Deliberately ignored -- see above.
      }
    },

    openForDate(dateStr) {
      // Re-asked on every opening: he may have followed the link, linked
      // in the other tab, and come straight back to this one.
      this.managerLinked = null;
      if ( this.form && this.form.recipientType === 'ride_manager' ) {
        this.refreshManagerLinked();
      }
      // dateStr omitted == "no day was chosen for me", which is the general
      // create button. The default is decided HERE rather than by the
      // caller, because the bounds belong to the date field and the date
      // field belongs to this component: a parent reaching in through
      // $refs for organizerMessageDateBounds was both the wrong direction
      // and an unguarded deref that would throw the moment the button's
      // v-if and this component's v-if stopped matching.
      //
      // Today when today is inside the window -- the likely intent
      // mid-trip -- and otherwise the earliest date the field will accept.
      if (!dateStr) {
        const bounds = this.organizerMessageDateBounds;
        const today = moment().format('YYYY-MM-DD');
        const withinWindow = bounds.min && bounds.max
          && today >= bounds.min && today <= bounds.max;
        dateStr = withinWindow ? today : (bounds.min || today);
      }
      if (this.isRideLocked) {
        this.showModalWithParameters({
          errorNumber: 'uesr_is_not_authorize_to_edit_this_ride',
          type: 'textConverter'
        });
        return;
      }
      this.form = {
        visible: true,
        mode: 'add',
        id: null,
        dayDate: dateStr,
        originalDayDate: dateStr,
        body: '',
        sendTime: '08:00',
        timezone: '',
        recipientType: 'registered_users',
        saving: false,
        error: ''
      };
    },

    openForMessage(message) {
      // Re-asked on every opening: he may have followed the link, linked
      // in the other tab, and come straight back to this one.
      this.managerLinked = null;
      if ( this.form && this.form.recipientType === 'ride_manager' ) {
        this.refreshManagerLinked();
      }
      if (this.isRideLocked) {
        this.showModalWithParameters({
          errorNumber: 'uesr_is_not_authorize_to_edit_this_ride',
          type: 'textConverter'
        });
        return;
      }
      this.form = {
        visible: true,
        mode: 'edit',
        id: message.id,
        dayDate: message.target_date || '',
        // What the modal OPENED with. buildOrganizerMessagePayload sends
        // target_date only when dayDate has moved away from this, so fixing
        // a typo never re-triggers the server's window check.
        originalDayDate: message.target_date || '',
        body: message.body,
        sendTime: message.send_time,
        timezone: message.timezone || '',
        recipientType: message.recipient_type || 'registered_users',
        saving: false,
        error: ''
      };
    },

    buildOrganizerMessagePayload() {
      // The create and edit endpoints read an ABSENT timezone differently
      // (NULL vs leave-unchanged), so the rule lives in a unit-tested util --
      // see src/utils/organizerMessagePayload.js. Getting it wrong sends a
      // message at the wrong hour while this modal reports success.
      return buildOrganizerMessagePayload(this.form, this.form.mode);
    },

    async saveOrganizerMessage() {
      const form = this.form;
      // `saving` disables the OK button, but Vue flushes that to the DOM on
      // nextTick, so a fast double-click can enter this handler twice before
      // the button goes dead. On the create path that is two rows with two
      // ids, hence two organizer-notify: namespaces, hence TWO billed
      // template messages to every traveller for one intended message. The
      // reactive flag cannot close that window; this early return can.
      if (form.saving) return;
      form.error = '';
      // An empty date is still worth refusing here. On create the server
      // answers invalid_target_date, but on EDIT the handler is PATCH-shaped:
      // an absent target_date means "leave unchanged", so a manager who
      // cleared the field would get a 200 and a modal that closed on a save
      // which silently kept the old day. Say so instead.
      if ( !form.dayDate ) {
        form.error = this.t( 'organizerMessageDateRequiredError' );
        return;
      }
      form.saving = true;
      try {
        const payload = this.buildOrganizerMessagePayload();
        let response;
        if (form.mode === 'add') {
          const rideId = this.allRide?.ride?.id;
          response = await organizerMessagesApi.create(this.apptoken, rideId, payload);
        } else {
          response = await organizerMessagesApi.update(this.apptoken, form.id, payload);
        }
        form.visible = false;
        this.$emit('saved', response.data);
      } catch (error) {
        // The server's refusal is PROOF he is not linked, whatever the
        // pre-check managed to find out -- so the link appears now even if
        // the lookup above failed or never ran.
        const code = error.response && error.response.data
          && error.response.data.error;
        if ( code === 'manager_whatsapp_not_linked' ) this.managerLinked = false;
        form.error = mapOrganizerMessageError(error, this.t);
      } finally {
        form.saving = false;
      }
    },

    zoneLabel(value) {
      // Mirrors zoneLabel in EditRideAccessKey.vue.
      let named = null;
      TIMEZONE_GROUPS.forEach((group) => {
        const found = group.zones.find((zone) => zone.value === value);
        if (found) {
          named = found;
        }
      });
      const name = named ? (this.isHebrew ? named.he : named.en) : value;
      const offset = offsetLabel(value);
      return offset ? `${name}  ${offset}` : name;
    }
  }
};
</script>

<style scoped>
/* All sizes in px -- html{font-size:10px} in this app makes rem misleading.
   That is also why everything Bootstrap draws for this modal is restyled
   below: the title, the field labels, the inputs and the Save/Cancel
   buttons are all sized in rem by Bootstrap's own CSS, so they came out at
   10-12.5px here. Matches DayManagerOptions.vue, 2026-10-05. */
.organizer-message-time-input {
  max-width: 180px;
}

.organizer-message-not-linked {
  display: block;
  margin-top: 8px;
  /* Larger than the 13px hints around it, and overriding <small>'s own
     shrink: this is a WARNING that something will not be delivered, not a
     footnote, and Hebrew is harder to read small. */
  font-size: 15px;
  line-height: 1.5;
  color: #b94a48;
}

.organizer-message-not-linked a {
  font-weight: 600;
  text-decoration: underline;
}

.organizer-message-time-help {
  display: block;
  margin-top: 5px;
  /* 13px was too small to read comfortably, in Hebrew especially. Still a
     step below the not-linked warning above, which has to out-rank a hint. */
  font-size: 14px;
  line-height: 1.5;
  color: #5A6B68;
}

.organizer-message-form-error {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #FDECEE;
  font-size: 15px;
  font-weight: 500;
  color: #B3202F;
}

.organizer-message-form :deep(.form-group) {
  margin-bottom: 18px;
}

.organizer-message-form :deep(.form-group:last-of-type) {
  margin-bottom: 0;
}

/* Mirrors the WHOLE dialog in Hebrew, header and footer included. Setting
   dir on the form alone left Bootstrap's header LTR, which put the close X
   on the right -- the same thing that was wrong in the day menu. */
:deep(.organizer-message-rtl) {
  direction: rtl;
  text-align: right;
}

:deep(.organizer-message-content) {
  border: 0;
  border-radius: 16px;
}

:deep(.organizer-message-content .modal-header) {
  padding: 18px 20px 12px;
  border-bottom: 0;
}

:deep(.organizer-message-content .modal-title) {
  font-size: 20px;
  font-weight: 600;
  color: #15312E;
}

:deep(.organizer-message-content .close) {
  /* 44px: a real touch target, this being tapped on a phone. */
  width: 44px;
  height: 44px;
  /* NOT Bootstrap's `margin-left: auto`. The header is a flex row with
     justify-content: space-between, so two children already sit at the two
     ends -- title at the inline-start, close at the inline-end, which
     mirrors correctly in Hebrew. The auto margin fought that: it resolved
     to 217px and parked the X in the middle of the header, right beside the
     title, with the whole left half empty. */
  margin: 0;
  margin-inline-end: -6px;
  padding: 0;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
  color: #5A6B68;
  opacity: 0.85;
}

:deep(.organizer-message-content .close:hover) {
  opacity: 1;
}

:deep(.organizer-message-content .modal-body) {
  padding: 4px 20px 18px;
}

:deep(.organizer-message-content label),
:deep(.organizer-message-content legend) {
  margin-bottom: 7px;
  font-size: 15px;
  font-weight: 600;
  color: #15312E;
}

:deep(.organizer-message-content .form-control),
:deep(.organizer-message-content .custom-select) {
  min-height: 46px;
  padding: 10px 12px;
  font-size: 16px;
  border-radius: 10px;
}

:deep(.organizer-message-content textarea.form-control) {
  line-height: 1.5;
}

/* A finger wherever a click does something. The date, time and timezone
   fields each open a picker on click (openNativePicker / the select's own),
   so they behave as buttons -- but a native input's default cursor says
   "type here", which is the wrong promise. The textarea deliberately keeps
   its text caret: that one really is for typing. Bootstrap already gives
   the close X and the footer buttons a pointer. */
:deep(.organizer-message-content input[type="date"]),
:deep(.organizer-message-content input[type="time"]),
:deep(.organizer-message-content .custom-select) {
  cursor: pointer;
}

/* Chrome draws the segments and the calendar/clock icon as shadow parts
   with their own cursor, so the host rule above does not reach them --
   without these, the finger flips back to an arrow over exactly the bits
   the manager aims at. */
:deep(.organizer-message-content input[type="date"]::-webkit-calendar-picker-indicator),
:deep(.organizer-message-content input[type="time"]::-webkit-calendar-picker-indicator),
:deep(.organizer-message-content input[type="date"]::-webkit-datetime-edit),
:deep(.organizer-message-content input[type="time"]::-webkit-datetime-edit),
:deep(.organizer-message-content input[type="date"]::-webkit-datetime-edit-text),
:deep(.organizer-message-content input[type="time"]::-webkit-datetime-edit-text),
:deep(.organizer-message-content input[type="date"]::-webkit-datetime-edit-day-field),
:deep(.organizer-message-content input[type="date"]::-webkit-datetime-edit-month-field),
:deep(.organizer-message-content input[type="date"]::-webkit-datetime-edit-year-field),
:deep(.organizer-message-content input[type="time"]::-webkit-datetime-edit-hour-field),
:deep(.organizer-message-content input[type="time"]::-webkit-datetime-edit-minute-field),
:deep(.organizer-message-content input[type="time"]::-webkit-datetime-edit-ampm-field) {
  cursor: pointer;
}

/* Bootstrap draws the select's chevron with a PHYSICAL background-position
   (right 7.5px), which does not follow direction. In Hebrew the text also
   starts at the right, so the two sat on top of each other. Move the
   chevron to the left and leave it room there. */
:deep(.organizer-message-rtl .custom-select) {
  padding-right: 12px;
  padding-left: 34px;
  background-position: left 12px center;
}

:deep(.organizer-message-content .modal-footer) {
  gap: 10px;
  padding: 10px 20px 18px;
  border-top: 0;
}

:deep(.organizer-message-content .modal-footer .btn) {
  /* >=44px: tapped on a phone. The footer's own margins would otherwise
     sit the two buttons flush against each other; gap above handles it. */
  min-height: 46px;
  margin: 0;
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
}
</style>
